import { Check, X, Minus } from "lucide-react";

const rows = [
  {
    label: "Cost predictability",
    diy: { icon: "check", note: "Lowest upfront" },
    freelance: { icon: "minus", note: "Varies per task" },
    agency: { icon: "check", note: "Retainer" },
    us: { icon: "check", note: "Clear packages" },
  },
  {
    label: "Consistency of quality",
    diy: { icon: "x", note: "Inconsistent" },
    freelance: { icon: "minus", note: "Depends on hire" },
    agency: { icon: "check", note: "Higher" },
    us: { icon: "check", note: "Systematized" },
  },
  {
    label: "Time burden on you",
    diy: { icon: "x", note: "Heavy" },
    freelance: { icon: "minus", note: "Managed by you" },
    agency: { icon: "check", note: "Lower" },
    us: { icon: "check", note: "One relationship" },
  },
  {
    label: "Agent-specific expertise",
    diy: { icon: "minus", note: "Self-taught" },
    freelance: { icon: "minus", note: "Rare" },
    agency: { icon: "minus", note: "Often generic" },
    us: { icon: "check", note: "Built for agents" },
  },
  {
    label: "Complete brand system",
    diy: { icon: "x", note: "Piece by piece" },
    freelance: { icon: "x", note: "One-off tasks" },
    agency: { icon: "check", note: "Sometimes siloed" },
    us: { icon: "check", note: "One system" },
  },
  {
    label: "Ongoing support",
    diy: { icon: "x", note: "None" },
    freelance: { icon: "x", note: "Project-based" },
    agency: { icon: "check", note: "Requires retainer" },
    us: { icon: "check", note: "Included" },
  },
  {
    label: "Listings, print, digital connected",
    diy: { icon: "x", note: "Disconnected" },
    freelance: { icon: "minus", note: "Rarely" },
    agency: { icon: "minus", note: "Sometimes" },
    us: { icon: "check", note: "Connected" },
  },
];

function Cell({ icon, note, highlight = false }) {
  const IconEl =
    icon === "check" ? Check : icon === "x" ? X : Minus;
  const color =
    icon === "check"
      ? highlight
        ? "text-[#C8A24B]"
        : "text-[#a1a1aa]"
      : icon === "x"
        ? "text-[#52525b]"
        : "text-[#71717a]";
  return (
    <div className="flex items-start gap-2.5 py-1">
      <IconEl size={16} strokeWidth={1.8} className={`${color} mt-0.5 flex-shrink-0`} />
      <span className={`text-xs md:text-[13px] ${highlight ? "text-white" : "text-[#a1a1aa]"}`}>
        {note}
      </span>
    </div>
  );
}

export default function ComparisonTable() {
  return (
    <div
      data-testid="comparison-table"
      className="overflow-x-auto -mx-6 md:mx-0"
    >
      <div className="min-w-[820px] md:min-w-0 px-6 md:px-0">
        <div className="grid grid-cols-5 gap-0 border border-[#27272a] rounded-sm overflow-hidden">
          {/* Header row */}
          <div className="p-5 md:p-6 border-b border-[#27272a] bg-[#0a0a0a]">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#71717a]">
              Approach
            </div>
          </div>
          <ColHeader label="DIY" />
          <ColHeader label="Freelancer" />
          <ColHeader label="Typical Agency" />
          <ColHeader label="Agent Marketing" highlight />

          {/* Data rows */}
          {rows.map((row, i) => (
            <div key={i} className="contents">
              <div
                className={`p-5 md:p-6 border-b border-[#27272a] ${
                  i % 2 === 0 ? "bg-[#0d0d0d]" : "bg-[#0a0a0a]"
                }`}
              >
                <div className="text-sm font-medium text-white">{row.label}</div>
              </div>
              <div
                className={`p-5 md:p-6 border-b border-[#27272a] ${
                  i % 2 === 0 ? "bg-[#0d0d0d]" : "bg-[#0a0a0a]"
                }`}
              >
                <Cell {...row.diy} />
              </div>
              <div
                className={`p-5 md:p-6 border-b border-[#27272a] ${
                  i % 2 === 0 ? "bg-[#0d0d0d]" : "bg-[#0a0a0a]"
                }`}
              >
                <Cell {...row.freelance} />
              </div>
              <div
                className={`p-5 md:p-6 border-b border-[#27272a] ${
                  i % 2 === 0 ? "bg-[#0d0d0d]" : "bg-[#0a0a0a]"
                }`}
              >
                <Cell {...row.agency} />
              </div>
              <div
                className={`p-5 md:p-6 border-b border-[#27272a] bg-[#C8A24B]/[0.04] border-l border-[#C8A24B]/40`}
              >
                <Cell {...row.us} highlight />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ColHeader({ label, highlight = false }) {
  return (
    <div
      className={`p-5 md:p-6 border-b border-[#27272a] ${
        highlight
          ? "bg-[#C8A24B]/[0.06] border-l border-[#C8A24B]/40"
          : "bg-[#0a0a0a]"
      }`}
    >
      <div
        className={`font-heading text-[13px] md:text-sm ${
          highlight ? "text-[#C8A24B]" : "text-white"
        }`}
      >
        {label}
      </div>
    </div>
  );
}
